import React, { useEffect, useState, forwardRef } from "react";
import { getUserById, getAllClients } from "../../core/api/settings";
import { useSelector } from "react-redux";
import { Form, message } from "antd";
import EditPermissionLayout from "./EditPermissionLayout";
import { getUserGroupById } from "../../core/api/auth";
import { getPagePermissions, sendOtpToEmail } from "../../core/api/auth";
import {
  verifyAdmin,
  checkUserDuplicates,
  updateUserPermissions,
} from "../../core/api/auth";
import { createUser, updateUser } from "../../core/api/settings";

// Constants moved to EditPermissionLayout.jsx

const EditPermission = forwardRef(
  (
    { isVisible, onClose, userId, fetchUsers, isViewMode, resetFormTrigger },
    ref
  ) => {
    const data = useSelector((state) => state.toggle_header);
    const profile = useSelector((state) => state.profile);

    // States
    const [userData, setUserData] = useState({
      username: "",
      email: "",
      role: "user",
      page_permissions: {}, // Changed from array to object for action-level permissions
      client_permission: [],
      first_name: "",
      last_name: "",
      password: "",
    });
    const [allClients, setAllClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    // Password states for form handling
    const [confirmPassword, setConfirmPassword] = useState("");
    const [adminPassword, setAdminPassword] = useState("");
    const isNewUser = !userId;
    const [pagePermissions, setPagePermissions] = useState({});
    // User groups are fetched but selection is optional
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [clientPermissions, setClientPermissions] = useState([]);
    const [selectedPage, setSelectedPage] = useState("");
    const [availableGroups, setAvailableGroups] = useState([]);
    const [showGroupManager, setShowGroupManager] = useState(false);
    const [pagePermissionOptions, setPagePermissionOptions] = useState([]);
    const [otpSent, setOtpSent] = useState(false);
    const [generatedOtp, setGeneratedOtp] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpExpiresAt, setOtpExpiresAt] = useState(null);
    const [remainingTime, setRemainingTime] = useState(0);
    const [enteredOtp, setEnteredOtp] = useState("");
    const [sendingOtp, setSendingOtp] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
      form.resetFields();
      setUserData({});
      setConfirmPassword("");
      setAdminPassword("");
      setOtpSent(false);
      setOtpVerified(false);
      setGeneratedOtp("");
      // No need to reset table data as we're using pagePermissions object directly
    }, [resetFormTrigger, form]);

    // Always load page permission options when the form becomes visible
    useEffect(() => {
      if (isVisible) {
        getPagePermissions()
          .then((permissions) => {
            console.log(
              "Loaded page permissions options when form opened:",
              permissions
            );
            setPagePermissionOptions(permissions);
          })
          .catch((err) => {
            console.error(
              "Failed to load page permission options when form opened:",
              err
            );
          });
      }
    }, [isVisible]);

    useEffect(() => {
      if (!selectedGroupId) {
        // Reset to original page permissions when no group is selected
        if (profile && profile.page_permissions) {
          getPagePermissions()
            .then(setPagePermissionOptions)
            .catch((err) => {
              console.error("Failed to load page permission options:", err);
              setPagePermissionOptions([]);
            });
        }
      }
    }, [selectedGroupId, profile]);

    const CLIENT_OPTIONS = allClients.map((client) => ({
      label: client.company_name,
      value: client.id,
    }));

    // ---- LOAD DATA ----
    // Clients
    useEffect(() => {
      getAllClients().then((res) => setAllClients(res.data || []));
    }, []);

    // User data
    useEffect(() => {
      const fetchUser = async () => {
        if (userId) {
          try {
            setLoading(true);
            const response = await getUserById(userId);
            const userData = response;
            setUserData(userData);
            form.setFieldsValue({
              email: userData?.email || "",
              role: userData?.role || "user",
              first_name: userData?.first_name || "",
              last_name: userData?.last_name || "",
            });
            console.log(userData, "user data after getting");
          } catch (error) {
            console.error("Failed to fetch user:", error);
          } finally {
            setLoading(false);
          }
        } else {
          // Reset for new user
          setUserData({
            username: "",
            email: "",
            role: "user",
            first_name: "",
            last_name: "",
            password: "",
            page_permissions: {},
            client_permission: [],
          });
          form.resetFields(); // clear form
        }
      };

      fetchUser();
    }, [userId, isVisible, form]);

    useEffect(() => {
      if (userId && isVisible && userData?.page_permissions) {
        const permissions = userData?.page_permissions || {};

        // Parse page_authorization from API response
        const authorizations = Array.isArray(userData?.page_authorization)
          ? userData.page_authorization
              .map((auth) => {
                const match = auth.match(/^(.+)_([a-z]+)\/\*$/i);
                return match ? { page: match[1], action: match[2] } : null;
              })
              .filter(Boolean)
          : [];

        console.log("User permissions from API:", permissions); // Debug log
        console.log("User authorizations from API:", authorizations); // Debug log

        setPagePermissions(permissions);

        // Use saved client permissions
        setClientPermissions(
          (userData?.client_permission || []).map((id) => parseInt(id, 10))
        );
      } else if (isVisible && !userId) {
        // For new users, start with empty permissions
        setPagePermissions({});
        // No need to reset table data as we're using pagePermissions object directly
        setClientPermissions([]);
        // Don't clear page permission options for new users
      }
    }, [userData, userId, isVisible, allClients]);

    //send OTP
    useEffect(() => {
      if (!otpSent || !otpExpiresAt) return;

      const interval = setInterval(() => {
        const timeLeft = Math.max(
          0,
          Math.floor((otpExpiresAt - Date.now()) / 1000)
        );
        setRemainingTime(timeLeft);

        if (timeLeft === 0) {
          setOtpSent(false);
          setGeneratedOtp("");
          setOtpExpiresAt(null);
          message.warning("OTP expired. Please request a new one.");
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [otpSent, otpExpiresAt]);

    const handleSendOtpClick = async () => {
      if (!userData?.email) {
        message.warning("Email is required to send OTP.");
        return;
      }

      setSendingOtp(true);
      try {
        const { otp } = await sendOtpToEmail(userData?.email || "");
        setGeneratedOtp(otp);
        setOtpSent(true);
        setOtpVerified(false);
        setOtpExpiresAt(new Date(Date.now() + 30 * 60000));
        message.success("OTP sent to user's email.");
      } catch (err) {
        message.error(err.message || "Failed to send OTP");
      } finally {
        setSendingOtp(false);
      }
    };

    const handleSelectPage = (page) => {
      console.log("Selected page:", page); // Debug log
      if (page === "__all__") {
        // Select all pages with default 'view' permission
        const allPageValues = pagePermissionOptions.map((p) => p.value);
        const newPermissions = { ...pagePermissions };

        allPageValues.forEach((pagePath) => {
          if (!newPermissions[pagePath]) {
            // If page doesn't exist in permissions, add it with default 'view' permission
            newPermissions[pagePath] = ["view"];
          }
        });

        setPagePermissions(newPermissions);
        setSelectedPage("");
      } else {
        setSelectedPage(page);

        // If new page, add it with default 'view' permission
        if (page && !pagePermissions[page]) {
          setPagePermissions((prev) => ({
            ...prev,
            [page]: ["view"], // Default to view permission
          }));
        }
      }
    };

    const handleTogglePermission = (action) => {
      if (!selectedPage) return;

      // Check if this page already has this permission
      const currentPermissions = pagePermissions[selectedPage] || [];
      const hasPermission = currentPermissions.includes(action);

      if (hasPermission) {
        // Remove the permission
        setPagePermissions((prev) => ({
          ...prev,
          [selectedPage]: currentPermissions.filter((p) => p !== action),
        }));
      } else {
        // Add the permission
        setPagePermissions((prev) => ({
          ...prev,
          [selectedPage]: [...currentPermissions, action],
        }));
      }
    };

    const handleRemovePage = (page) => {
      // Remove the page from the permissions object
      setPagePermissions((prev) => {
        const updatedPermissions = { ...prev };
        delete updatedPermissions[page];
        return updatedPermissions;
      });

      // Clear selected page if it was the one removed
      if (selectedPage === page) setSelectedPage("");
    };

    // Select all permissions for a single page row
    const handleSelectAllForPage = (page) => {
      if (!page) {
        message.error("Page not found");
        return;
      }

      const allPermissions = ["view", "edit", "create", "delete", "download"];

      // Force update by creating new object and array references
      setPagePermissions((prev) => {
        const updated = { ...prev };
        updated[page] = [...allPermissions]; // New array reference
        return updated;
      });
    };

    // Select all permissions for ALL pages in the table
    const handleSelectAllPermissions = () => {
      const allPermissions = ["view", "edit", "create", "delete", "download"];
      setPagePermissions((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((page) => {
          updated[page] = allPermissions;
        });
        return updated;
      });
      message.success(
        `All permissions added to ${Object.keys(pagePermissions).length} pages`
      );
    };

    // Add all pages with all permissions
    const handleAddAllPagesWithAllPermissions = async () => {
      const allPermissions = ["view", "edit", "create", "delete", "download"];

      // Always fetch fresh list to ensure we get ALL routes
      try {
        const allRouteOptions = await getPagePermissions();
        console.log(
          `Adding all ${allRouteOptions.length} routes with full access`
        );

        const allPages = {};
        let skippedCount = 0;

        allRouteOptions.forEach((opt) => {
          if (opt && opt.value) {
            allPages[opt.value] = allPermissions;
          } else {
            skippedCount++;
            console.warn("Skipped route option (missing value):", opt);
          }
        });

        // Verify we got all routes
        if (skippedCount > 0) {
          console.warn(
            `⚠️ Skipped ${skippedCount} routes due to missing values`
          );
        }

        setPagePermissions(allPages);
        setPagePermissionOptions(allRouteOptions); // Update options too

        const addedCount = Object.keys(allPages).length;
        message.success(
          `Added all ${addedCount} pages with full access (view, edit, create, delete, download)`
        );

        console.log(
          `✅ Successfully added ${addedCount} routes with full permissions`
        );
      } catch (error) {
        console.error("Failed to load all routes:", error);
        message.error("Failed to load all routes. Please try again.");
      }
    };

    // Convert permissions object to table data format
    const tableData = Object.entries(pagePermissions).map(
      ([page, actions], i) => {
        const normalizePath = (path) =>
          path.replace(/\/\*$/, "").replace(/\/$/, "");

        const matchedOption = pagePermissionOptions.find(
          (opt) => normalizePath(opt.value) === normalizePath(page)
        );

        return {
          key: i,
          page: matchedOption?.label || page, // label or fallback
          pageRaw: page, // raw value for operations
          actions: actions, // Array of action permissions for this page
        };
      }
    );

    const handleSavePermission = async (values) => {
      setLoading(true);
      try {
        const errors = [];
        const hasValue = (v) => v?.trim?.() !== "";
        if (!hasValue(values.email)) {
          errors.push({ name: "email", errors: ["Email is required."] });
          message.error("Email is required.");
        }

        if (!values.role) {
          errors.push({ name: "role", errors: ["Role is required."] });
          message.error("Role is required.");
        }

        if (isNewUser || values.password) {
          const weakPasswords = [
            "12345678",
            "password",
            "admin123",
            "qwerty123",
            "letmein",
          ];
          const strongRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

          if (!values.password) {
            errors.push({
              name: "password",
              errors: ["Password is required."],
            });
            message.error("Password is required.");
          } else if (weakPasswords.includes(values.password.toLowerCase())) {
            errors.push({
              name: "password",
              errors: ["Password is too common or insecure."],
            });
            message.error("Password is too common or insecure.");
          } else if (!strongRegex.test(values.password)) {
            errors.push({
              name: "password",
              errors: [
                "Password must have at least 8 characters, including uppercase, lowercase, number, and special character.",
              ],
            });
            message.error("Weak password format.");
          }

          if (values.password !== confirmPassword) {
            errors.push({
              name: "confirm_password",
              errors: ["Passwords do not match."],
            });
            message.error("Passwords do not match.");
          }
        }
        form.setFields(errors);

        const hasBlockingErrors = errors.some((e) =>
          ["email"].includes(e.name)
        );
        if (hasBlockingErrors) {
          return;
        }

        if (isNewUser) {
          const duplicates = await checkUserDuplicates({
            email: values.email,
            user_id: null,
          });

          if (duplicates.email_exists) {
            form.setFields([
              {
                name: "email",
                errors: ["This email is already in use."],
              },
            ]);
            message.error("This email is already in use.");
            return;
          }
        }

        if (!isNewUser && values.password) {
          if (!adminPassword) {
            form.setFields([
              { name: "admin_password", errors: ["Admin password required."] },
            ]);
            message.error("Please enter your admin password.");
            return;
          }

          const result = await verifyAdmin(adminPassword);
          if (!result.valid) {
            form.setFields([
              { name: "admin_password", errors: ["Incorrect password."] },
            ]);
            message.error("Your admin password is incorrect.");
            return;
          }
        }

        // Merge page permissions with main module permissions

        console.log("Final page permissions to save:", pagePermissions);

        const payload = {
          username: values.email,
          email: values.email,
          role: values.role,
          first_name: values.first_name,
          last_name: values.last_name,
          // Store all page permissions in the page_permissions field with action-level permissions
          page_permissions: pagePermissions,
          client_permission: clientPermissions,
        };

        if (values.password) payload.password = values.password;
        if (!isNewUser && values.password)
          payload.admin_password = adminPassword;

        if (isNewUser) {
          // Create the user and get the response which should contain the new user ID
          const newUserResponse = await createUser(payload);

          // Also update route permissions in the new permission system if we have the user ID
          if (newUserResponse && newUserResponse.id) {
            try {
              // Pass the structured permissions object directly
              await updateUserPermissions(newUserResponse.id, pagePermissions);
              console.log(
                "Successfully updated permissions for new user:",
                newUserResponse.id
              );
            } catch (permError) {
              console.error("Error updating route permissions:", permError);
              // Don't fail the whole operation if this fails
            }
          }
          message.success(`${values.email} has been created successfully.`);
        } else {
          await updateUser(userId, payload);

          // Update route permissions in the new permission system
          try {
            // Pass the structured permissions object directly
            await updateUserPermissions(userId, pagePermissions);
            console.log(
              "Successfully updated permissions for existing user:",
              userId
            );
          } catch (permError) {
            console.error("Error updating route permissions:", permError);
            // Don't fail the whole operation if this fails
          }

          message.success(`${values.email} has been updated successfully.`);
        }

        fetchUsers();
        onClose();
      } catch (error) {
        console.error("Error updating/creating user:", error);
        const errorMessage =
          error?.response?.data?.role?.[0] ||
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not update/create user. Please try again later.";
        message.error(errorMessage);

        // Set form field errors if they exist
        if (error?.response?.data) {
          const fieldErrors = [];
          Object.keys(error.response.data).forEach((key) => {
            const errorValue = error.response.data[key];
            if (Array.isArray(errorValue)) {
              fieldErrors.push({ name: key, errors: errorValue });
            } else if (typeof errorValue === "string") {
              fieldErrors.push({ name: key, errors: [errorValue] });
            }
          });
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (!selectedGroupId) {
        if (profile && profile.page_permissions) {
          getPagePermissions()
            .then((permissions) => {
              console.log("Loaded page permissions:", permissions); // Debug log
              setPagePermissionOptions(permissions);
            })
            .catch((err) => {
              console.error("Failed to load page permission options:", err);
              setPagePermissionOptions([]);
            });
        } else {
          setPagePermissionOptions([]);
        }
      }
    }, [selectedGroupId, profile]);

    const handleRoleChange = (role) => {
      setUserData({ ...userData, role });
      setSelectedGroupId(null);

      if (role === "admin" || role === "super_admin") {
        if (profile && profile.page_permissions) {
          getPagePermissions()
            .then((allPermissions) => {
              console.log("Admin permissions loaded:", allPermissions); // Debug log
              setPagePermissionOptions(allPermissions);

              // Create permissions object with all pages having view permission
              const permissionsObj = {};
              allPermissions.forEach((opt) => {
                if (opt && opt.value) {
                  permissionsObj[opt.value] = ["view"];
                }
              });

              setPagePermissions(permissionsObj);
            })
            .catch((err) => {
              console.error("Failed to load page permission options:", err);
              setPagePermissionOptions([]);
            });
        }
      } else if (role === "senior_accountant" || role === "management") {
        // Auto-select ALL permissions for senior_accountant and management
        getPagePermissions()
          .then((allPermissions) => {
            console.log(
              `Auto-selecting all permissions for ${role}. Found ${allPermissions.length} routes.`
            );
            setPagePermissionOptions(allPermissions);

            // Auto-select ALL pages with ALL permissions
            const permissionsObj = {};
            const fullAccess = ["view", "edit", "create", "delete", "download"];
            let skippedCount = 0;

            allPermissions.forEach((opt) => {
              if (opt && opt.value) {
                permissionsObj[opt.value] = fullAccess;
              } else {
                skippedCount++;
                console.warn("Skipped route option (missing value):", opt);
              }
            });

            if (skippedCount > 0) {
              console.warn(
                `⚠️ Skipped ${skippedCount} routes due to missing values`
              );
            }

            setPagePermissions(permissionsObj);

            // Auto-select ALL clients
            const allClientIds = allClients.map((client) => client.id);
            setClientPermissions(allClientIds);

            const addedCount = Object.keys(permissionsObj).length;
            console.log(
              `✅ Auto-selected ${addedCount} pages (with all 5 permissions each) and ${allClientIds.length} clients`
            );
          })
          .catch((err) => {
            console.error("Failed to load page permission options:", err);
            setPagePermissionOptions([]);
          });
      } else if (role === "user" || role === "normal_user") {
        // For regular users, load options but don't auto-select
        if (profile && profile.page_permissions) {
          getPagePermissions()
            .then((allPermissions) => {
              setPagePermissionOptions(allPermissions);
            })
            .catch((err) => {
              console.error("Failed to load page permission options:", err);
              setPagePermissionOptions([]);
            });
        }
        if (
          !userData?.page_permissions ||
          Object.keys(userData.page_permissions).length === 0
        ) {
          setPagePermissions({});
        }
        console.log(`${role} role selected`);
      }
    };

    useEffect(() => {
      const fetchGroupDetails = async () => {
        if (!selectedGroupId) {
          console.log("No group selected, clearing page options"); // Debug log
          return;
        }

        try {
          const group = await getUserGroupById(selectedGroupId);
          console.log("Fetched group details:", group);
          const mainPages = (group.page_permissions || []).map((path) =>
            path.replace(/\/\*$/, "").replace(/\/$/, "")
          );
          console.log("Main pages from group:", mainPages);
          const subRoutes = getSubRoutesForMainPages(mainPages);
          setPagePermissionOptions(subRoutes);
        } catch (err) {
          console.error("Failed to fetch group details:", err);
        }
      };

      fetchGroupDetails();
    }, [selectedGroupId]);

    const getSubRoutesForMainPages = (mainPages) => {
      const subRoutes = [];

      mainPages.forEach((page) => {
        const cleanPage = page.replace(/\/\*$/, "").replace(/\/$/, "");

        if (
          cleanPage === "/accounting" ||
          cleanPage.startsWith("/accounting/")
        ) {
          subRoutes.push(
            { label: "Accounting", value: "/accounting" },
            { label: "Accounting - Dashboard", value: "/accounting/dashboard" },
            { label: "Accounting - Reports", value: "/accounting/reports" },
            {
              label: "Accounting - Profit & Loss Report",
              value: "/profit-loss-report",
            },
            {
              label: "Accounting - Balance Sheet Report",
              value: "/balance-sheet-report",
            },
            {
              label: "Accounting - Aging Report",
              value: "/accounting/aging-report",
            },
            {
              label: "Accounting - Accounts Payable",
              value: "/accounting/accounts-payable",
            },
            {
              label: "Accounting - Accounts Receivable",
              value: "/accounting/accounts-receivable",
            },
            {
              label: "Accounting - Transactions & Allocations",
              value: "/accounting/transactions-allocations",
            },
            {
              label: "Accounting - Advance Accounting",
              value: "/accounting/advance-accounting",
            },
            { label: "Accounting - Settings", value: "/accounting/settings" },
            { label: "Accounting - Invoices", value: "/accounting/invoices" },
            {
              label: "Accounting - Create Invoice",
              value: "/accounting/create-invoice",
            },
            {
              label: "Accounting - Actions History",
              value: "/accounting/actions-history",
            },
            { label: "Accounting - Aspire", value: "/accounting/aspire" },
            {
              label: "Accounting - Payment Allocation",
              value: "/accounting/payment-allocation",
            },
            {
              label: "Accounting - Bank Statements",
              value: "/accounting/payment-allocation/bank-statements",
            },
            {
              label: "Accounting - Statements History",
              value: "/accounting/payment-allocation/statements-history",
            },
            {
              label: "Accounting - Credits",
              value: "/accounting/payment-allocation/credits",
            },
            {
              label: "Accounting - Cash",
              value: "/accounting/payment-allocation/cash",
            },
            {
              label: "Accounting - Create Credit",
              value: "/accounting/payment-allocation/create-credit",
            },
            {
              label: "Accounting - Create Cash",
              value: "/accounting/payment-allocation/create-cash",
            }
          );
        }

        if (cleanPage === "/sales" || cleanPage.startsWith("/sales/")) {
          subRoutes.push(
            { label: "Sales - Dashboard", value: "/sales/dashboard" },
            { label: "Sales - Orders", value: "/sales/orders" },
            { label: "Sales - Reports", value: "/sales/reports" }
          );
        }

        if (cleanPage === "/products" || cleanPage.startsWith("/products/")) {
          subRoutes.push(
            { label: "Products - List", value: "/products/list" },
            { label: "Products - Add", value: "/products/add" }
          );
        }
      });

      return subRoutes;
    };

    const generateStrongPassword = () => {
      const lowercase = "abcdefghijklmnopqrstuvwxyz";
      const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const digits = "0123456789";
      const special = "!@#$%^&*()+";
      const all = lowercase + uppercase + digits + special;
      const getRandomChar = (set) =>
        set.charAt(Math.floor(Math.random() * set.length));

      const requiredChars = [
        getRandomChar(lowercase),
        getRandomChar(uppercase),
        getRandomChar(digits),
        getRandomChar(special),
      ];

      while (requiredChars.length < 12) {
        requiredChars.push(getRandomChar(all));
      }

      for (let i = requiredChars.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [requiredChars[i], requiredChars[j]] = [
          requiredChars[j],
          requiredChars[i],
        ];
      }

      return requiredChars.join("");
    };

    const handleCancel = () => {
      form.resetFields();
      setUserData({});
      setConfirmPassword("");
      setAdminPassword("");
      onClose();
    };

    const handleClientChange = (selectedValues) => {
      setClientPermissions(selectedValues);
    };

    useEffect(() => {
      if (isVisible && !userId) {
        // New user - start clean
        setPagePermissions({});
        // Load page permission options for new users
        if (profile && profile.page_permissions) {
          getPagePermissions()
            .then((permissions) => {
              console.log("Loaded page permissions for new user:", permissions);
              setPagePermissionOptions(permissions);
            })
            .catch((err) => {
              console.error(
                "Failed to load page permission options for new user:",
                err
              );
              setPagePermissionOptions([]);
            });
        }
        setSelectedGroupId(null);
        console.log("New user form opened"); // Debug log
      }
    }, [isVisible, userId, profile]);
    const handleTogglePermissionInTable = (page, action) => {
      // Check if this page exists in permissions and if the action is already added
      const pagePermissionsExist = pagePermissions[page];
      const alreadyAdded =
        pagePermissionsExist && pagePermissions[page].includes(action);

      console.log(
        `Toggle permission for ${page}, action: ${action}, current state: ${
          alreadyAdded ? "enabled" : "disabled"
        }`
      );

      setPagePermissions((prev) => {
        const updatedPermissions = { ...prev };

        // If page doesn't exist in permissions yet, initialize it with view permission
        if (!updatedPermissions[page]) {
          updatedPermissions[page] = [];
        }

        if (alreadyAdded) {
          // Remove the action from permissions
          updatedPermissions[page] = updatedPermissions[page].filter(
            (a) => a !== action
          );

          // If no permissions left for this page, remove the page entirely
          if (updatedPermissions[page].length === 0) {
            delete updatedPermissions[page];
          }
        } else {
          // Add the action to permissions
          updatedPermissions[page] = [...updatedPermissions[page], action];
        }

        return updatedPermissions;
      });
    };

    // Check if user is editing themselves
    const isEditingSelf = userId && profile && userId === profile.id;

    return (
      <EditPermissionLayout
        ref={ref}
        isVisible={isVisible}
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        onClose={onClose}
        isNewUser={isNewUser}
        isViewMode={isViewMode}
        userData={userData}
        setUserData={setUserData}
        form={form}
        loading={loading}
        data={data}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        adminPassword={adminPassword}
        setAdminPassword={setAdminPassword}
        selectedPage={selectedPage}
        handleSelectPage={handleSelectPage}
        isEditingSelf={isEditingSelf}
        pagePermissionOptions={pagePermissionOptions}
        selectedGroupId={selectedGroupId}
        pagePermissions={pagePermissions}
        handleTogglePermission={handleTogglePermission}
        tableData={tableData}
        handleTogglePermissionInTable={handleTogglePermissionInTable}
        handleRemovePage={handleRemovePage}
        handleSelectAllForPage={handleSelectAllForPage}
        handleSelectAllPermissions={handleSelectAllPermissions}
        handleAddAllPagesWithAllPermissions={
          handleAddAllPagesWithAllPermissions
        }
        CLIENT_OPTIONS={CLIENT_OPTIONS}
        clientPermissions={clientPermissions}
        handleClientChange={handleClientChange}
        availableGroups={availableGroups}
        setSelectedGroupId={setSelectedGroupId}
        setShowGroupManager={setShowGroupManager}
        showGroupManager={showGroupManager}
        generateStrongPassword={generateStrongPassword}
        handleSavePermission={handleSavePermission}
        handleCancel={handleCancel}
        otpVerified={otpVerified}
        otpSent={otpSent}
        sendingOtp={sendingOtp}
        handleSendOtpClick={handleSendOtpClick}
        enteredOtp={enteredOtp}
        setEnteredOtp={setEnteredOtp}
        generatedOtp={generatedOtp}
        remainingTime={remainingTime}
        handleRoleChange={handleRoleChange}
        setOtpVerified={setOtpVerified}
      />
    );
  }
);

EditPermission.displayName = "EditPermission";
export default EditPermission;
